import { getCookie, setCookie } from 'cookies-next';
import { Currency } from 'graphql/config';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useConfigQuery } from 'utils/api/useConfigQuery';
import { replaceUrl } from 'utils/helpers/replaceUrl';

export const DEFAULT_CURRENCY_CODE = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';
export const DEFAULT_CURRENCY_ID = 3064;

let hasSetInitialCurrency = false;

export function useCurrency() {
  const { data } = useConfigQuery();
  const { i18n } = useTranslation();
  const { language } = i18n;

  if (!hasSetInitialCurrency) {
    hasSetInitialCurrency = true;
    setInitialCurrency();
  }

  return {
    currencies: data?.currencies,
    findCurrencyBy: findBy(data?.currencies ?? []),
    formatAmount: formatAmount(language),
    getCurrencyId: useMemo(() => getId(data?.currencies), [data?.currencies]),
    getCurrencySymbol: useMemo(() => getSymbol(language), [language]),
    selectedCurrency: getSelectedCurrencyCode(),
    setCurrency,
    symbolPlacement,
  };
}

function findBy(currencies: Array<Currency>) {
  return function (key: keyof Currency, value: string | number) {
    return currencies.find(currency => currency[key] === value);
  };
}

function symbolPlacement(code: string, language: string) {
  return currencyParts(language, code).findIndex(format => format.type === 'currency') > 0 ? 'after' : 'before';
}

export function getSelectedCurrencyCode() {
  if (typeof window === 'undefined') {
    return DEFAULT_CURRENCY_CODE;
  }
  return (new URLSearchParams(location.search).get('currency') ?? DEFAULT_CURRENCY_CODE).toUpperCase();
}

async function setCurrency(currency?: string) {
  setCookie('currency', currency);
  await replaceUrl({ currency });
}

function setInitialCurrency() {
  if (typeof window === 'undefined' || new URLSearchParams(location.search).get('currency')) {
    return;
  }
  void setCurrency(getCookie('currency'));
}

function getSymbol(language: string) {
  return function (code?: string) {
    return getCurrencyPart('currency', language, code)?.value ?? DEFAULT_CURRENCY_SYMBOL;
  };
}

export function getCurrencyPart(type: string, language: string, code?: string) {
  return currencyParts(language, code).find(f => f.type === type);
}

function currencyFormat(language: string, code?: string, decimalPlaces?: number) {
  return new Intl.NumberFormat(language, {
    currency: code ?? getSelectedCurrencyCode(),
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: decimalPlaces,
    minimumFractionDigits: decimalPlaces,
    style: 'currency',
  });
}

function currencyParts(language: string, code?: string) {
  return currencyFormat(language, code).formatToParts(0.0);
}

function getId(currencies: Array<Currency> | undefined) {
  return function (code: string) {
    return currencies?.find(c => c.code === code)?.id ?? DEFAULT_CURRENCY_ID;
  };
}

function formatAmount(language: string) {
  return function ({ amount, decimalPlaces, code }: { amount: number; code?: string; decimalPlaces?: number }) {
    return currencyFormat(language, code, decimalPlaces).format(amount);
  };
}
